// Admin index route — '/admin/'.
//
// Overview dashboard, ordered to answer "am I on air?" first:
//
//   1. Flows (`admin-overview-flows`) — on-air / armed flow cards.
//   2. Streams (`admin-overview-streams`) — live vs offline key counts.
//   3. System flags (`admin-overview-system`) — Broadcasting, Encoding,
//      Recording and Transcription as Switch toggles wired to the same
//      POST routes the Settings page uses. Turning a guarded flag off
//      while keys are live asks first.
//   4. Recordings (`admin-overview-recordings`) — count, size, scheduled
//      plays and a link to the Recordings tab.
//
// All HTTP goes through `apiClient`; no raw `fetch`. Every scoped read
// and write follows the layout's view-as selection. Toggle mutations
// invalidate the shared `['admin', 'system-settings']` key prefix so the
// Settings page (same prefix) stays in sync.
import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  FlowStateCard,
  LiveFlowCard,
} from '@/components/flows/FlowStateCard'
import { InlineError, InlineEmpty } from '@/components/ui'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Label } from '@/components/ui/label'
import { SectionCard } from '@/components/ui/section-card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { ApiError, apiClient, scopedApiPath, toFormDataPhp } from '@/lib/api-client'
import { countLabel, formatBytes } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useViewAsStore } from '@/stores/view-as-store'
import type {
  FlowListItem,
  FlowsListResponse,
  RecordingSchedulesResponse,
  RecordingsResponse,
  StreamSummary,
  SystemSettingMutationResponse,
  SystemSettings,
} from '@/lib/types'

export const Route = createFileRoute('/admin/')({
  component: AdminIndexPage,
})

// `/api/streams/` historically returns either a bare array or
// `{ streams: [] }` depending on the legacy PHP variant. Normalize both
// shapes via a `select` projection so the widget always sees a plain array.
type StreamsListResponse = StreamSummary[] | { streams?: StreamSummary[] }

function normalizeStreams(payload: StreamsListResponse): StreamSummary[] {
  if (Array.isArray(payload)) return payload
  return payload.streams ?? []
}

// ── Page ────────────────────────────────────────────────────────────

function AdminIndexPage() {
  // View-as is owned by the admin layout header. Every scoped read and
  // write on this page follows it, so a super admin impersonating another
  // operator never flips their own system from the overview.
  const viewAsId = useViewAsStore((s) => s.viewAsId)
  const vaSuffix: string = viewAsId == null ? '' : `view_as=${String(viewAsId)}`
  const buildPath = (base: string): string => scopedApiPath(base, viewAsId)

  const streamsQuery = useQuery<StreamsListResponse, Error, StreamSummary[]>({
    queryKey: ['admin', 'overview', 'streams'],
    queryFn: () => apiClient.get<StreamsListResponse>('/streams/'),
    select: normalizeStreams,
  })

  // Cache key shares the prefix `['admin','system-settings']` with the
  // Settings page so any sub-card POST there refreshes this widget too.
  const systemQuery = useQuery<SystemSettings>({
    queryKey: ['admin', 'system-settings', vaSuffix],
    queryFn: () => apiClient.get<SystemSettings>(buildPath('/system/settings/')),
  })

  const recordingsQuery = useQuery<RecordingsResponse, Error>({
    queryKey: ['admin', 'overview', 'recordings', vaSuffix],
    queryFn: () => apiClient.get<RecordingsResponse>(buildPath('/recordings/')),
  })

  const schedulesQuery = useQuery<RecordingSchedulesResponse, Error>({
    queryKey: ['admin', 'overview', 'recordings-schedule', vaSuffix],
    queryFn: () =>
      apiClient.get<RecordingSchedulesResponse>(buildPath('/recordings/schedule/')),
  })

  // Flows — shares the ['flows'] cache with /flows and /admin/streams when
  // not impersonating; re-keys under view-as.
  const flowsQuery = useQuery({
    queryKey: vaSuffix === '' ? ['flows'] : ['flows', vaSuffix],
    queryFn: () => apiClient.get<FlowsListResponse>(buildPath('/flows/')),
    refetchInterval: 5000,
  })
  const flows: FlowListItem[] = flowsQuery.data?.flows ?? []
  const activeFlows = flows.filter((flow) => Boolean(flow.ACTIVE))
  const onAirCount = activeFlows.filter((flow) => flow.LIVE?.connected).length
  const armedCount = activeFlows.length - onAirCount
  const flowStatusDescription =
    activeFlows.length > 0
      ? `${countLabel(onAirCount, 'flow')} on air · ${armedCount} armed and waiting for a signal.`
      : 'Only armed flows appear here. Arm one on the Flows tab to allocate its keys.'

  // "Next up": among armed flows not currently live, the one armed most recently.
  const nextUpFlowId = (() => {
    let best: FlowListItem | null = null
    for (const flow of activeFlows) {
      if (flow.LIVE !== null || !flow.ACTIVATED_AT) continue
      if (best === null || flow.ACTIVATED_AT > (best.ACTIVATED_AT ?? '')) {
        best = flow
      }
    }
    return best?.ID ?? null
  })()

  const liveStreams: StreamSummary[] =
    streamsQuery.data?.filter((s) => s.is_live) ?? []
  const offlineStreams: StreamSummary[] =
    streamsQuery.data?.filter((s) => !s.is_live) ?? []

  const system = systemQuery.data?.system
  const broadcasting = system?.broadcasting ?? false
  const encoding = system?.encoding ?? false
  const recording = system?.recording ?? false
  const transcription = system?.transcription ?? false

  const recordings = recordingsQuery.data?.recordings ?? []
  const recordingsTotalBytes = recordings.reduce((acc, r) => acc + (r.size ?? 0), 0)
  const allSchedules = schedulesQuery.data?.schedules ?? []
  const upcomingCount = allSchedules.filter(
    (s) => s.status === 'pending' || s.status === 'active',
  ).length

  // Two cards must never leave a dead third column: only open the third
  // column when there are three or more.
  const flowGridClass = cn(
    'grid items-start gap-4 grid-cols-1 sm:grid-cols-2',
    activeFlows.length >= 3 && 'xl:grid-cols-3',
  )

  return (
    <div data-testid="admin-index" className="space-y-4">
      <SectionCard
        title="Flows"
        description={flowStatusDescription}
        data-testid="admin-overview-flows"
        compact
      >
        {flowsQuery.isLoading ? (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-44 w-full" />
            ))}
          </div>
        ) : flowsQuery.isError ? (
          <InlineError message="Couldn't load flows." />
        ) : flows.length === 0 ? (
          <InlineEmpty message="No flows yet. Build one on the Flows tab." />
        ) : activeFlows.length === 0 ? (
          <InlineEmpty message="Nothing armed. Arm a flow on the Flows tab to allocate its keys." />
        ) : (
          <div className={flowGridClass}>
            {activeFlows.map((flow) =>
              flow.LIVE ? (
                <LiveFlowCard key={flow.ID} flow={flow} />
              ) : (
                <FlowStateCard
                  key={flow.ID}
                  flow={flow}
                  isNextUp={flow.ID === nextUpFlowId}
                />
              ),
            )}
          </div>
        )}
      </SectionCard>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <SectionCard
          title="Stream keys"
          description="Keys configured for this account."
          compact
          contentClassName="flex flex-col gap-3"
        >
          <div data-testid="admin-overview-streams" className="flex-1">
            {streamsQuery.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-1/2" />
                <Skeleton className="h-4 w-1/3" />
              </div>
            ) : streamsQuery.isError ? (
              <InlineError message="Couldn't load stream keys." />
            ) : (
              <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span
                  className={cn(
                    'text-2xl font-semibold leading-none whitespace-nowrap',
                    liveStreams.length > 0 ? 'text-cyber-green' : 'text-foreground',
                  )}
                >
                  <span data-testid="admin-overview-streams-live" className="font-mono tabular-nums">{liveStreams.length}</span>{' '}
                  live
                </span>
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  <span data-testid="admin-overview-streams-offline" className="font-mono tabular-nums">{offlineStreams.length}</span>{' '}
                  offline
                </span>
              </p>
            )}
          </div>
          <Button asChild variant="outline" size="sm" className="w-fit">
            <Link to="/admin/streams">Manage keys</Link>
          </Button>
        </SectionCard>

        <SectionCard
          title="System flags"
          description="Pipeline stages for every live key. Turning one off while keys are live asks first."
          compact
          className="lg:col-span-2"
        >
          <div
            data-testid="admin-overview-system"
            className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4"
          >
            {systemQuery.isLoading ? (
              <>
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </>
            ) : systemQuery.isError ? (
              <InlineError
                message="Couldn't load system flags."
                className="sm:col-span-2 xl:col-span-4"
              />
            ) : (
              <>
                <SystemFlagToggle
                  label="Broadcasting"
                  checked={broadcasting}
                  endpoint={buildPath('/system/settings/broadcasting/')}
                  testIdPrefix="admin-overview-system-broadcasting"
                  liveKeyCount={liveStreams.length}
                  confirmOff={{
                    title: 'Turn broadcasting off?',
                    description:
                      'Relaying to every broadcast destination stops immediately for the keys that are live. Viewers on those platforms lose the stream.',
                    confirmLabel: 'Turn off broadcasting',
                  }}
                />
                <SystemFlagToggle
                  label="Encoding"
                  checked={encoding}
                  endpoint={buildPath('/system/settings/encoding/')}
                  testIdPrefix="admin-overview-system-encoding"
                  liveKeyCount={liveStreams.length}
                  confirmOff={{
                    title: 'Turn encoding off?',
                    description:
                      'The encoder stops for the keys that are live. HLS viewers lose the stream until encoding is turned back on.',
                    confirmLabel: 'Turn off encoding',
                  }}
                />
                <SystemFlagToggle
                  label="Recording"
                  checked={recording}
                  endpoint={buildPath('/system/settings/recording/')}
                  testIdPrefix="admin-overview-system-recording"
                  liveKeyCount={liveStreams.length}
                  confirmOff={{
                    title: 'Turn recording off?',
                    description:
                      'Recordings in progress for the keys that are live end now. Footage already written is kept.',
                    confirmLabel: 'Turn off recording',
                  }}
                />
                <SystemFlagToggle
                  label="Transcription"
                  checked={transcription}
                  endpoint={buildPath('/system/settings/transcription/')}
                  testIdPrefix="admin-overview-system-transcription"
                  liveKeyCount={liveStreams.length}
                />
              </>
            )}
          </div>
        </SectionCard>
      </div>

      <SectionCard
        title="Recordings"
        description="Stored files and scheduled front-page plays."
        data-testid="admin-overview-recordings"
        compact
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {recordingsQuery.isLoading || schedulesQuery.isLoading ? (
            <Skeleton className="h-6 w-56" />
          ) : recordingsQuery.isError ? (
            <InlineError message="Couldn't load recordings." className="flex-1" />
          ) : (
            <p className="flex flex-wrap items-baseline gap-x-2 text-sm text-muted-foreground">
              <span className="text-lg font-semibold leading-none text-foreground whitespace-nowrap">
                <span className="font-mono tabular-nums">{recordings.length}</span>{' '}
                {recordings.length === 1 ? 'recording' : 'recordings'}
              </span>
              <span className="whitespace-nowrap">· <span className="font-mono tabular-nums">{formatBytes(recordingsTotalBytes)}</span></span>
              <span
                data-testid="admin-overview-recordings-schedule"
                className="whitespace-nowrap"
              >
                · {schedulesQuery.isError ? 'schedules unavailable' : <><span className="font-mono tabular-nums">{upcomingCount}</span> scheduled</>}
              </span>
            </p>
          )}
          <Button asChild variant="outline" size="sm" className="ml-auto">
            <Link to="/admin/recordings">View recordings</Link>
          </Button>
        </div>
      </SectionCard>
    </div>
  )
}

// ── Internal helpers ────────────────────────────────────────────────

// Switch-backed toggle that POSTs {status:'0'|'1'} to a system-settings
// endpoint and re-fetches the shared aggregate so both this card and
// the Settings page reflect the new value. The widget stays bound to
// the server value (controlled via `checked`) so a mid-flight toggle
// can't visually drift from the cache.
function SystemFlagToggle({
  label,
  checked,
  endpoint,
  testIdPrefix,
  liveKeyCount,
  confirmOff,
}: {
  label: string
  checked: boolean
  endpoint: string
  testIdPrefix: string
  /** Keys currently live; turning a guarded flag off while any are live asks first. */
  liveKeyCount: number
  /** When set, turning the flag OFF while keys are live opens this confirmation. */
  confirmOff?: { title: string; description: string; confirmLabel: string }
}) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const mutation = useMutation({
    mutationFn: (next: boolean) =>
      apiClient.post<SystemSettingMutationResponse>(
        endpoint,
        toFormDataPhp({ status: next ? '1' : '0' }),
      ),
    onSuccess: () => {
      setError(null)
      setConfirming(false)
      void qc.invalidateQueries({ queryKey: ['admin', 'system-settings'] })
    },
    onError: (err) =>
      setError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Unknown error.',
      ),
  })

  const switchId = `${testIdPrefix}-switch`
  const pending = mutation.isPending

  const requestChange = (next: boolean): void => {
    if (!next && confirmOff && liveKeyCount > 0) {
      setError(null)
      setConfirming(true)
      return
    }
    mutation.mutate(next)
  }

  return (
    <div
      data-testid={testIdPrefix}
      className="flex flex-col gap-2 rounded-md border border-border/60 bg-background/40 px-3 py-2"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <Label
            htmlFor={switchId}
            className="text-sm font-medium text-foreground"
          >
            {label}
          </Label>
          <p className="mt-0.5 text-xs text-muted-foreground" aria-live="polite">
            {pending ? 'Saving…' : checked ? 'On' : 'Off'}
          </p>
        </div>
        <Switch
          id={switchId}
          data-testid={`${testIdPrefix}-switch`}
          checked={checked}
          disabled={pending}
          aria-busy={pending}
          onCheckedChange={requestChange}
        />
      </div>
      {error !== null && (
        <InlineError
          message={error}
          data-testid={`${testIdPrefix}-error`}
          className="text-xs"
        />
      )}
      {confirmOff ? (
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title={confirmOff.title}
          description={`${liveKeyCount} ${liveKeyCount === 1 ? 'key is' : 'keys are'} live right now. ${confirmOff.description}`}
          confirmLabel={confirmOff.confirmLabel}
          busyLabel="Turning off…"
          confirmVariant="destructive-solid"
          busy={pending}
          error={confirming ? error : null}
          onConfirm={() => mutation.mutate(false)}
          data-testid={`${testIdPrefix}-confirm`}
        />
      ) : null}
    </div>
  )
}
